import { useCallback, useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import { useMount } from "app/custom-hooks/useMount";
import type { LeadPriorityScore } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import useSaveRules from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useSaveRules";
import { useScoringBoardCrud } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringBoardCrud";
import { useScoringBoardData } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringBoardData";
import { useScoringBoardTrackingService } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringBoardTrackingService";
import { getScoringConfigSignalSource } from "app/pages/sales-intelligence/modules/lead-scoring-agent/utils/getScoringConfigSignalSource";
import { prepareWizardRuleUpdate } from "app/pages/sales-intelligence/modules/lead-scoring-agent/utils/scoringBoardUtils";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import {
    resetSignalsSourceAction,
    setSignalsSourceAction,
} from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";
import type { IScoringRuleDto } from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";
import { ScoringConfigType } from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";

type FlowStep = { type: "visibility" } | { type: "wizard"; configType: ScoringConfigType };

type UseLeadScoreScoringRuleFlowOptions = {
    priority: LeadPriorityScore;
    onCreated: (priority: LeadPriorityScore) => void;
    onClose: () => void;
};

/**
 * Drives the scoring-rule creation flow (visibility choice → wizard → save) for one priority. It is mounted by
 * the page-root host only while a rule is being created, so its scoring-config queries never load at page load.
 */
export const useLeadScoreScoringRuleFlow = ({ priority, onCreated, onClose }: UseLeadScoreScoringRuleFlowOptions) => {
    const dispatch = useDispatch();
    const canManageShared = salesSettingsHelper.isHighLevelAccount();
    const scoringBoardTracking = useScoringBoardTrackingService();
    const { saveRules: saveUserRules, isPending: isSavingUser } = useSaveRules(ScoringConfigType.User);
    const { saveRules: saveAccountRules, isPending: isSavingAccount } = useSaveRules(ScoringConfigType.Account);
    const { persist } = useScoringBoardCrud();
    const { rules: userRules, isFetching: isUserFetching } = useScoringBoardData(ScoringConfigType.User);
    const { rules: accountRules, isFetching: isAccountFetching } = useScoringBoardData(ScoringConfigType.Account);

    // Users who can author shared rules pick visibility first; everyone else goes straight to the user-scoped wizard.
    const [step, setStep] = useState<FlowStep>(() =>
        canManageShared ? { type: "visibility" } : { type: "wizard", configType: ScoringConfigType.User },
    );

    useMount(() => scoringBoardTracking.trackAddRule(priority));

    const wizardConfigType = step.type === "wizard" ? step.configType : null;

    useEffect(() => {
        if (wizardConfigType === null) {
            return;
        }

        dispatch(setSignalsSourceAction(getScoringConfigSignalSource(wizardConfigType)));

        return () => {
            dispatch(resetSignalsSourceAction());
        };
    }, [dispatch, wizardConfigType]);

    const handleVisibilityNext = useCallback(
        (configType: ScoringConfigType) => setStep({ type: "wizard", configType }),
        [],
    );

    const handleWizardSave = useCallback(
        (rule: IScoringRuleDto) => {
            if (step.type !== "wizard") {
                return;
            }

            const { configType } = step;
            const rules = configType === ScoringConfigType.User ? userRules : accountRules;
            const saveRules = configType === ScoringConfigType.User ? saveUserRules : saveAccountRules;
            const { nextRules, successKey } = prepareWizardRuleUpdate(rules, { mode: "create", priority }, rule);

            persist(saveRules, nextRules, successKey, () => {
                scoringBoardTracking.trackSaveRuleWizard("create");
                onCreated(priority);
                onClose();
            });
        },
        [
            step,
            priority,
            userRules,
            accountRules,
            saveUserRules,
            saveAccountRules,
            persist,
            scoringBoardTracking,
            onCreated,
            onClose,
        ],
    );

    return {
        step,
        isSaving: isSavingUser || isSavingAccount,
        isFetching: isUserFetching || isAccountFetching,
        handleVisibilityNext,
        handleWizardSave,
    };
};
